import type { IndustryDetail } from "@/types/industryDetail";
import {
  Landmark,
  Users,
  Rocket,
  Contact,
  Building2,
  MapPin,
  Gauge,
  Zap,
  LineChart,
  ShieldCheck,
  Clock,
} from "@/icons";

export const industriesB: IndustryDetail[] = [
  {
    slug: "government",
    name: "Government & Public Sector",
    sector: "Public Services",
    tagline: "Modernise public services with secure digital solutions that improve citizen experience, automate government workflows and increase operational transparency.",
    icon: Landmark,
    accent: "#334155",
    tags: ["Citizen Services", "Approvals", "Assets", "Documents", "Audit"],
    image: "/images/industries/Government.webp",
    overview:
      "Government organisations manage a wide range of citizen services, administrative processes, public assets and regulatory responsibilities while maintaining high standards of security, transparency and accessibility. Many departments still rely on paper-based workflows, legacy applications and disconnected systems that slow service delivery and increase administrative costs. We develop secure digital government solutions that automate workflows, improve interdepartmental collaboration, enhance citizen engagement and provide real-time visibility across government operations.",
    painPoints: [
      "Paper-based citizen services and administrative workflows.",
      "Legacy applications that are difficult to integrate and maintain.",
      "Limited visibility across departments and public services.",
      "Lengthy approval processes and manual document handling.",
      "Increasing cybersecurity, compliance and accessibility requirements.",
    ],
    manualProcesses: [
      "Processing applications on paper.",
      "Routing approvals via internal mail.",
      "Re-entering data between departments.",
      "Compiling performance reports by hand.",
    ],
    opportunities: [
      {
          title: "Citizen Digital Services",
          description: "Provide secure online services that reduce paperwork, waiting times and administrative effort."
      },
      {
          title: "Workflow Automation",
          description: "Automate approvals, document management and government business processes across departments."
      },
      {
          title: "Integrated Government Platforms",
          description: "Connect departments through secure APIs and shared digital services for improved collaboration."
      },
      {
          title: "Operational Intelligence",
          description: "Use dashboards, analytics and AI-powered reporting to improve policy implementation and operational decision-making."
      },
    ],
    recommendedServices: ["web-applications", "workflow-automation", "cloud-deployment", "ai-integration", "api-development"],
    recommendedSolutions: ["visitor-management", "asset-management", "customer-portal"],
    techStack: ["React", "Python", "FastAPI", "PostgreSQL", "Redis", "Docker", "REST APIs", "OpenAI", "n8n", "Single Sign-On (SSO)"],
    typicalUsers: ["Citizens", "Government Officers", "Department Managers", "Administrators", "Senior Government Officials", "Public Service Executives"],
    workflow: [
      { label: "Request", description: "Citizens submit applications online through accessible forms." },
      { label: "Route", description: "Cases are assigned and approvals routed automatically." },
      { label: "Process", description: "Officers handle cases with full history and documents." },
      { label: "Notify", description: "Applicants receive status updates at each step." },
      { label: "Report", description: "Dashboards track service levels and outcomes transparently." },
    ],
    benefits: [
      {
          title: "Improve Citizen Experience",
          description: "Provide faster, more convenient digital services that reduce queues and simplify interactions with government departments.",
          icon: Clock,
      },
      {
          title: "Increase Operational Efficiency",
          description: "Automated workflows reduce paperwork, improve collaboration and accelerate service delivery.",
          icon: Zap,
      },
      {
          title: "Strengthen Security & Compliance",
          description: "Secure architectures, audit trails and role-based access controls help protect sensitive government information.",
          icon: ShieldCheck,
      },
      {
          title: "Enhance Transparency & Decision Making",
          description: "Real-time dashboards and analytics provide better visibility into service delivery, operational performance and public programmes.",
          icon: LineChart,
      },
    ],
    outcomes: [
      {label: "Reduction in Case Processing Time", value: "40%"},
      {label: "Increase in Digital Service Adoption", value: "55%",},
      {label: "Accessibility Compliance", value: "WCAG 2.2 AA"}
    ],
    faqs: [
      {
          question: "Can your solutions integrate with our existing government systems?",
          answer: "Yes. We develop solutions that integrate with existing government applications, legacy systems and third-party platforms using secure APIs and modern integration approaches, allowing organisations to modernise gradually without replacing every existing system."
      },
      {
          question: "How do you ensure security for government applications?",
          answer: "Security is a fundamental part of every solution we build. We implement secure authentication, role-based access control, encryption, audit trails, logging and other industry best practices to help protect sensitive government information."
      },
      {
          question: "Can government workflows and approvals be automated?",
          answer: "Yes. We automate approvals, document processing, case management and administrative workflows to reduce paperwork, accelerate service delivery and improve operational efficiency."
      },
      {
          question: "Do you develop citizen self-service portals?",
          answer: "Yes. We build secure citizen portals that allow users to submit applications, upload documents, track request status, receive notifications and access government services online."
      },
      {
          question: "Can AI improve government operations?",
          answer: "Yes. AI can assist with document classification, intelligent search, workflow automation, reporting and operational analytics. We implement AI solutions where they provide measurable value while maintaining appropriate governance and transparency."
      },
      {
          question: "Can your applications support multiple departments?",
          answer: "Yes. Our platforms are designed to support multiple departments with shared services, centralised reporting and department-specific workflows while maintaining appropriate access controls."
      },
      {
          question: "Do your applications comply with accessibility standards?",
          answer: "Yes. We design user interfaces that follow recognised accessibility principles and can support standards such as WCAG, helping ensure government services remain accessible to all users."
      },
      {
          question: "Can projects be delivered in phases?",
          answer: "Absolutely. Government digital transformation projects are typically implemented in phases, allowing departments to minimise disruption while gradually expanding digital capabilities."
      },
    ],
    caseConcept: {
      title: "Government Digital Services Platform",
      category: "Example Project",
      summary:
          "A government department relied on paper-based processes, manual approvals and disconnected legacy systems for citizen services. We implemented an integrated digital platform that automated workflows, provided secure citizen self-service capabilities and delivered real-time dashboards for operational monitoring and reporting.",
      metrics: [
          {
              label: "Reduction in Case Processing Time",
              value: "40%",
          },
          {
              label: "Increase in Online Service Adoption",
              value: "55%",
          },
          {
              label: "Government Departments",
              value: "12",
          },
      ],
      tech: [
          "React",
          "FastAPI",
          "PostgreSQL",
          "Docker",
      ],
      accent: "#334155",
    },
  },
  {
    slug: "startups",
    name: "Startups",
    sector: "Early-Stage & Scale-Up",
    tagline: "Ship a credible MVP fast — then scale it without a rebuild.",
    icon: Rocket,
    accent: "#7C3AED",
    tags: ["MVP", "SaaS", "AI", "Cloud", "Payments", "Analytics"],
    image: "/images/industries/Startup.webp",
    overview:
      "Startups need to move fast without painting themselves into a corner. We help you launch a focused MVP, learn from real users and scale on foundations built to grow.",
    painPoints: [
      "Pressure to launch fast without wasting runway.",
      "Technical debt that blocks scaling later.",
      "No in-house senior design or engineering yet.",
      "Investor and user expectations for polish.",
      "Uncertainty about what to build first.",
    ],
    manualProcesses: [
      "Managing early ops in spreadsheets.",
      "Onboarding users by hand.",
      "Manual billing and reporting.",
      "Ad-hoc analytics gathering.",
    ],
    opportunities: [
      { title: "Focused MVP", description: "Launch the smallest thing that proves value fast." },
      { title: "Scalable foundation", description: "Architecture that grows without a costly rewrite." },
      { title: "Design credibility", description: "A polished product that earns user and investor trust." },
      { title: "Built-in analytics", description: "Learn from real usage from day one." },
    ],
    recommendedServices: ["web-applications", "ui-ux-design", "cloud-deployment"],
    recommendedSolutions: ["crm", "customer-portal", "erp"],
    techStack: ["React", "TypeScript", "FastAPI", "PostgreSQL", "Stripe", "Cloud"],
    typicalUsers: ["Founders", "Early team", "First customers", "Investors"],
    workflow: [
      { label: "Define", description: "We pin down the core value and smallest viable scope." },
      { label: "Design", description: "A credible, polished experience is prototyped and validated." },
      { label: "Build", description: "The MVP ships fast on scalable foundations." },
      { label: "Launch", description: "You go live with analytics to learn from real users." },
      { label: "Scale", description: "We iterate and harden as traction grows." },
    ],
    benefits: [
      { title: "Faster to market", description: "Prove value before runway runs out.", icon: Zap },
      { title: "Investor-ready", description: "Polish that builds confidence.", icon: ShieldCheck },
      { title: "No rebuild later", description: "Scale on the same foundation.", icon: Rocket },
      { title: "Learn quickly", description: "Analytics guide what to build next.", icon: LineChart },
    ],
    outcomes: [
      { label: "To MVP", value: "8-10 wks" },
      { label: "Activation", value: "+40%" },
      { label: "Rework", value: "Minimal" },
    ],
    faqs: [
      { question: "How fast can we launch an MVP?", answer: "Typically eight to ten weeks for a focused first version." },
      { question: "Will it scale if we get traction?", answer: "Yes, we build on foundations designed to grow." },
      { question: "Can you act as our product team?", answer: "Yes, we provide senior design and engineering end to end." },
      { question: "Do we own the code and IP?", answer: "Yes, fully — with documentation for your team." },
      { question: "Can you help decide what to build?", answer: "Yes, discovery pins down the highest-value scope." },
      { question: "Is it investor-ready?", answer: "Yes, polish and reliability that build confidence." },
      { question: "Can you integrate payments and auth?", answer: "Yes, billing, auth and analytics are standard." },
      { question: "What if priorities change mid-build?", answer: "Our iterative process lets you steer every week." },
    ],
    caseConcept: {
      title: " Signal — SaaS MVP",
      category: "Startups",
      summary: "A concept MVP launched in ten weeks with billing and analytics built in, ready to scale as traction grew.",
      metrics: [{ label: "To MVP", value: "10 wks" }, { label: "Activation", value: "+40%" }, { label: "Uptime", value: "99.9%" }],
      tech: ["React", "FastAPI", "Stripe"],
      accent: "#7C3AED",
    },
  },
  {
    slug: "hospitality",
    name: "Hospitality & Tourism",
    sector: "Travel & Leisure",
    tagline: "Delight guests from booking to checkout across every property.",
    icon: MapPin,
    accent: "#0891B2",
    tags: ["Bookings", "Restaurant", "POS", "Inventory", "Housekeeping"],
    image: "/images/industries/Hospitality_Tourism.webp",
    overview:
      "Hospitality wins on seamless, personal guest experiences. We build systems that unify bookings, operations and guest data so every stay feels effortless and every property runs smoothly.",
    painPoints: [
      "Bookings spread across channels and prone to overbooking.",
      "Disconnected front-desk, housekeeping and F&B systems.",
      "No unified guest profile to personalise stays.",
      "Manual check-in creating queues at peak times.",
      "Limited visibility across multiple properties.",
    ],
    manualProcesses: [
      "Reconciling bookings across channels.",
      "Coordinating housekeeping on paper.",
      "Checking guests in manually.",
      "Compiling occupancy reports by hand.",
    ],
    opportunities: [
      { title: "Unified bookings", description: "One calendar across channels prevents overbooking." },
      { title: "Digital check-in", description: "Fast, contactless arrival removes queues." },
      { title: "Guest 360", description: "Personalise stays with a complete guest profile." },
      { title: "Operations sync", description: "Front desk, housekeeping and F&B work in step." },
    ],
    recommendedServices: ["web-applications", "mobile-app-development", "ai-integration"],
    recommendedSolutions: ["restaurant-management", "visitor-management", "customer-portal"],
    techStack: ["React", "React Native", "FastAPI", "PostgreSQL", "Payments", "Cloud"],
    typicalUsers: ["Front desk", "Housekeeping", "F&B teams", "Managers"],
    workflow: [
      { label: "Book", description: "Reservations sync across channels into one calendar." },
      { label: "Arrive", description: "Guests check in fast, in person or contactless." },
      { label: "Stay", description: "Requests and services are coordinated across teams." },
      { label: "Dine", description: "F&B and amenities are billed seamlessly to the room." },
      { label: "Depart", description: "Checkout is smooth and guest data informs the next stay." },
    ],
    benefits: [
      { title: "Happier guests", description: "Seamless, personal experiences lift reviews.", icon: LineChart },
      { title: "No overbooking", description: "One synced calendar across channels.", icon: ShieldCheck },
      { title: "Faster check-in", description: "Digital arrival removes queues.", icon: Zap },
      { title: "Smoother ops", description: "Teams coordinate in real time.", icon: Gauge },
    ],
    outcomes: [
      { label: "Check-in", value: "-50%" },
      { label: "Reviews", value: "+0.4★" },
      { label: "Overbooking", value: "~0" },
    ],
    faqs: [
      { question: "Can it sync bookings across channels?", answer: "Yes, one calendar prevents overbooking across OTAs and direct." },
      { question: "Does it support digital check-in?", answer: "Yes, fast in-person or contactless arrival." },
      { question: "Can it personalise guest stays?", answer: "Yes, a guest 360 view powers personalisation." },
      { question: "Does it connect housekeeping and F&B?", answer: "Yes, operations are coordinated in real time." },
      { question: "Is it multi-property capable?", answer: "Yes, manage several properties centrally." },
      { question: "Does it integrate payments?", answer: "Yes, room billing and payments are supported." },
      { question: "Can guests use a mobile app?", answer: "Yes, for booking, check-in and requests." },
      { question: "How soon can we launch?", answer: "A focused first release is typically live within a few months." },
    ],
    caseConcept: {
      title: "Azure Stays — hospitality suite",
      category: "Hospitality & Tourism",
      summary: "A concept suite unifying bookings, check-in and operations across properties, halving check-in time and ending overbooking.",
      metrics: [{ label: "Check-in", value: "-50%" }, { label: "Reviews", value: "+0.4★" }, { label: "Properties", value: "4" }],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0891B2",
    },
  },
  // {
  //   slug: "non-profit",
  //   name: "Non-Profit / NGO",
  //   sector: "Social Impact",
  //   tagline: "Do more with less — manage donors, programmes and volunteers in one place.",
  //   icon: Users,
  //   accent: "#059669",
  //   tags: ["Donors", "Fundraising", "Volunteers", "Events", "Reporting"],
  //   overview:
  //     "Non-profits stretch limited resources across fundraising, programmes and reporting. We build affordable systems that grow donations, coordinate volunteers and prove impact.",
  //   painPoints: [
  //     "Donor data scattered across spreadsheets.",
  //     "Manual, time-consuming grant and impact reporting.",
  //     "Volunteer coordination handled by email.",
  //     "Limited budget for expensive software licences.",
  //     "Hard to show funders measurable impact.",
  //   ],
  //   manualProcesses: [
  //     "Tracking donations in spreadsheets.",
  //     "Scheduling volunteers over email.",
  //     "Compiling impact reports by hand.",
  //     "Sending receipts manually.",
  //   ],
  //   opportunities: [
  //     { title: "Donor management", description: "Grow and retain donors with a clear relationship view." },
  //     { title: "Programme tracking", description: "Coordinate activities and measure outcomes." },
  //     { title: "Volunteer portal", description: "Simple scheduling and communication for volunteers." },
  //     { title: "Impact reporting", description: "Prove results to funders with data, not guesswork." },
  //   ],
  //   recommendedServices: ["web-applications", "crm-development", "workflow-automation"],
  //   recommendedSolutions: ["crm", "customer-portal", "hrms"],
  //   techStack: ["React", "FastAPI", "PostgreSQL", "Stripe", "Email/SMS", "Cloud"],
  //   typicalUsers: ["Fundraisers", "Programme staff", "Volunteers", "Trustees"],
  //   workflow: [
  //     { label: "Engage", description: "Supporters and donors are captured in one clear view." },
  //     { label: "Donate", description: "Online giving and receipts are handled automatically." },
  //     { label: "Coordinate", description: "Volunteers and programmes are scheduled and tracked." },
  //     { label: "Deliver", description: "Activities and outcomes are recorded as they happen." },
  //     { label: "Report", description: "Impact dashboards make funder reporting effortless." },
  //   ],
  //   benefits: [
  //     { title: "More donations", description: "Better donor relationships lift giving.", icon: LineChart },
  //     { title: "Lower cost", description: "Affordable, fit-for-purpose tooling.", icon: Gauge },
  //     { title: "Less admin", description: "Automation frees staff for the mission.", icon: Clock },
  //     { title: "Proven impact", description: "Data-backed reports funders trust.", icon: ShieldCheck },
  //   ],
  //   outcomes: [
  //     { label: "Admin time", value: "-45%" },
  //     { label: "Donor retention", value: "+20%" },
  //     { label: "Report time", value: "-70%" },
  //   ],
  //   faqs: [
  //     { question: "Is this affordable for a non-profit?", answer: "Yes, we scope to your budget and avoid costly per-seat licences." },
  //     { question: "Can it manage donors and gift aid?", answer: "Yes, donor records, receipts and giving history are covered." },
  //     { question: "Does it help with impact reporting?", answer: "Yes, dashboards make funder reporting fast and credible." },
  //     { question: "Can volunteers self-schedule?", answer: "Yes, a volunteer portal handles scheduling and comms." },
  //     { question: "Does it integrate with payment providers?", answer: "Yes, for online donations and recurring giving." },
  //     { question: "Is supporter data secure?", answer: "Yes, with encryption and role-based access." },
  //     { question: "Can we start small?", answer: "Yes, begin with one area and expand over time." },
  //     { question: "Do we own the system?", answer: "Yes, you own the code, data and roadmap." },
  //   ],
  //   caseConcept: {
  //     title: "Open Hands — non-profit CRM",
  //     category: "Non-Profit / NGO",
  //     summary: "A concept CRM unifying donors, volunteers and impact reporting, lifting retention and slashing reporting time.",
  //     metrics: [{ label: "Retention", value: "+20%" }, { label: "Admin", value: "-45%" }, { label: "Reports", value: "-70%" }],
  //     tech: ["React", "FastAPI", "PostgreSQL"],
  //     accent: "#059669",
  //   },
  // },
  // {
  //   slug: "professional-services",
  //   name: "Professional Services",
  //   sector: "Consulting & Agencies",
  //   tagline: "Win, deliver and bill work — with clients who feel looked after.",
  //   icon: Contact,
  //   accent: "#0F766E",
  //   tags: ["CRM", "Projects", "Billing", "Timesheets", "Clients", "Reports"],
  //   overview:
  //     "Firms live and die by utilisation and client trust. We build systems that manage pipeline, projects, time and billing so your team bills more and clients stay longer.",
  //   painPoints: [
  //     "Pipeline and projects tracked in disconnected tools.",
  //     "Time and expenses captured late or not at all.",
  //     "Billing that's slow and error-prone.",
  //     "No clear view of utilisation and profitability.",
  //     "Client communication scattered across email.",
  //   ],
  //   manualProcesses: [
  //     "Logging timesheets in spreadsheets.",
  //     "Chasing approvals over email.",
  //     "Assembling invoices by hand.",
  //     "Compiling project status manually.",
  //   ],
  //   opportunities: [
  //     { title: "Pipeline & CRM", description: "Track opportunities and forecast with confidence." },
  //     { title: "Project delivery", description: "Plan, resource and monitor engagements in one place." },
  //     { title: "Time & billing", description: "Capture time effortlessly and bill accurately, faster." },
  //     { title: "Client portal", description: "Give clients a professional, transparent experience." },
  //   ],
  //   recommendedServices: ["web-applications", "crm-development", "workflow-automation"],
  //   recommendedSolutions: ["crm", "hrms", "customer-portal"],
  //   techStack: ["React", "FastAPI", "PostgreSQL", "Stripe", "SSO", "Cloud"],
  //   typicalUsers: ["Partners", "Consultants", "Project managers", "Finance"],
  //   workflow: [
  //     { label: "Win", description: "Opportunities are tracked and forecast in the pipeline." },
  //     { label: "Plan", description: "Projects are scoped and resourced against availability." },
  //     { label: "Deliver", description: "Work, time and expenses are captured as they happen." },
  //     { label: "Bill", description: "Accurate invoices are generated from tracked time." },
  //     { label: "Retain", description: "A client portal keeps relationships transparent." },
  //   ],
  //   benefits: [
  //     { title: "Higher utilisation", description: "See and improve billable capacity.", icon: LineChart },
  //     { title: "Faster billing", description: "Invoice from tracked time in minutes.", icon: Zap },
  //     { title: "Fewer leaks", description: "Capture all billable time and expenses.", icon: Gauge },
  //     { title: "Stronger retention", description: "A polished client experience builds loyalty.", icon: ShieldCheck },
  //   ],
  //   outcomes: [
  //     { label: "Utilisation", value: "+15%" },
  //     { label: "Billing time", value: "-60%" },
  //     { label: "Leakage", value: "-35%" },
  //   ],
  //   faqs: [
  //     { question: "Does it handle time and billing?", answer: "Yes, effortless time capture flows straight into invoices." },
  //     { question: "Can it track pipeline and forecasts?", answer: "Yes, CRM and forecasting are built in." },
  //     { question: "Is there a client portal?", answer: "Yes, for transparent, professional client communication." },
  //     { question: "Can it manage resourcing?", answer: "Yes, plan projects against real team availability." },
  //     { question: "Does it integrate with accounting?", answer: "Yes, billing flows to your finance system." },
  //     { question: "Will it show profitability?", answer: "Yes, by project, client and consultant." },
  //     { question: "Can it scale with the firm?", answer: "Yes, it grows with headcount and complexity." },
  //     { question: "Do we own the system?", answer: "Yes, code, data and roadmap are yours." },
  //   ],
  //   caseConcept: {
  //     title: "Atlas Advisory — services platform",
  //     category: "Professional Services",
  //     summary: "A concept platform unifying pipeline, delivery and billing for a consultancy, lifting utilisation and speeding invoicing.",
  //     metrics: [{ label: "Utilisation", value: "+15%" }, { label: "Billing", value: "-60%" }, { label: "Leakage", value: "-35%" }],
  //     tech: ["React", "FastAPI", "PostgreSQL"],
  //     accent: "#0F766E",
  //   },
  // },
  // {
  //   slug: "construction",
  //   name: "Construction & Infrastructure",
  //   sector: "Built Environment",
  //   tagline: "Keep projects, assets and compliance on track from site to sign-off.",
  //   icon: Building2,
  //   accent: "#EA580C",
  //   tags: ["Projects", "Contracts", "Equipment", "Labour", "Safety"],
  //   overview:
  //     "Construction runs on coordination across sites, subcontractors and heavy assets. We build systems that keep projects on schedule, assets accounted for and compliance airtight.",
  //   painPoints: [
  //     "Project progress tracked on spreadsheets and calls.",
  //     "Equipment and materials lost or double-booked.",
  //     "Safety and compliance records kept on paper.",
  //     "Subcontractor coordination that's slow and manual.",
  //     "Cost overruns spotted too late to act.",
  //   ],
  //   manualProcesses: [
  //     "Updating project schedules by hand.",
  //     "Logging site inspections on paper.",
  //     "Tracking plant and equipment in spreadsheets.",
  //     "Collecting timesheets manually.",
  //   ],
  //   opportunities: [
  //     { title: "Project tracking", description: "Live progress, schedule and cost across every site." },
  //     { title: "Asset & plant", description: "Know where equipment is and when it's serviced." },
  //     { title: "Digital compliance", description: "Safety checks and records, audit-ready." },
  //     { title: "Field mobile apps", description: "Capture updates and photos from site instantly." },
  //   ],
  //   recommendedServices: ["web-applications", "mobile-app-development", "workflow-automation"],
  //   recommendedSolutions: ["asset-management", "inventory-management", "hrms"],
  //   techStack: ["React", "React Native", "FastAPI", "PostgreSQL", "Maps", "Cloud"],
  //   typicalUsers: ["Project managers", "Site supervisors", "Subcontractors", "Finance"],
  //   workflow: [
  //     { label: "Plan", description: "Projects are scheduled with tasks, resources and budgets." },
  //     { label: "Assign", description: "Crews, subcontractors and equipment are allocated." },
  //     { label: "Track", description: "Site progress, photos and inspections are captured live." },
  //     { label: "Control", description: "Costs and safety are monitored against plan." },
  //     { label: "Handover", description: "Complete, audit-ready records close out the project." },
  //   ],
  //   benefits: [
  //     { title: "On-schedule delivery", description: "Live tracking keeps projects on time.", icon: Clock },
  //     { title: "Fewer losses", description: "Asset tracking cuts loss and double-booking.", icon: ShieldCheck },
  //     { title: "Cost control", description: "Spot overruns early enough to act.", icon: Gauge },
  //     { title: "Safer sites", description: "Digital compliance keeps records airtight.", icon: LineChart },
  //   ],
  //   outcomes: [
  //     { label: "Overruns", value: "-25%" },
  //     { label: "Asset loss", value: "-35%" },
  //     { label: "Admin", value: "-40%" },
  //   ],
  //   faqs: [
  //     { question: "Can it track multiple projects and sites?", answer: "Yes, progress, cost and resources across all sites." },
  //     { question: "Does it manage plant and equipment?", answer: "Yes, location, assignment and maintenance are tracked." },
  //     { question: "Can crews use it on site?", answer: "Yes, mobile apps capture updates, photos and inspections." },
  //     { question: "Does it help with safety compliance?", answer: "Yes, digital checks and audit-ready records." },
  //     { question: "Can it control costs?", answer: "Yes, live cost tracking against budget flags overruns early." },
  //     { question: "Does it handle subcontractors?", answer: "Yes, coordination and approvals are streamlined." },
  //     { question: "Does it work with limited connectivity?", answer: "Field apps support offline capture and later sync." },
  //     { question: "Do we own the system?", answer: "Yes, code, data and roadmap are yours." },
  //   ],
  //   caseConcept: {
  //     title: "Keystone Build — project control",
  //     category: "Construction & Infrastructure",
  //     summary: "A concept platform tracking projects, plant and compliance across sites, reducing overruns and asset loss.",
  //     metrics: [{ label: "Overruns", value: "-25%" }, { label: "Asset loss", value: "-35%" }, { label: "Admin", value: "-40%" }],
  //     tech: ["React", "FastAPI", "PostgreSQL"],
  //     accent: "#EA580C",
  //   },
  // },
];
